import type { CSSProperties, RefObject } from 'react'
import { Group, Text, TextInput, Tooltip, UnstyledButton, VisuallyHidden } from '@mantine/core'
import { faPenToSquare } from '@fortawesome/sharp-duotone-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { formatTaskDate } from '../../utils/formatters'
import { toDateInputValue, tomorrowDateInputValue } from './TaskCardHelpers'

export interface TaskHoldDateRowProps {
  taskType: string
  holdDate: string | null
  // True while a picked date has not come back from the API yet.
  changed: boolean
  expanded: boolean
  // False while the hold is in force: the on-hold proc refuses to reschedule it.
  canEdit: boolean
  editing: boolean
  // Why the field shows a date the card declined to commit; null while it agrees.
  error?: string | null
  inputRef: RefObject<HTMLInputElement | null>
  onToggleEdit: () => void
  onSelect: (value: string) => void
}

// Once rescheduled, the hold date is highlighted so the change is obvious. Weight and
// background carry it as well as colour, so the state is never colour alone.
function getHoldDateStyle(changed: boolean): CSSProperties {
  if (!changed) return { whiteSpace: 'nowrap', color: 'var(--mantine-color-dimmed)' }
  return {
    whiteSpace: 'nowrap',
    color: 'var(--mantine-color-brand-7)',
    fontWeight: 'var(--mantine-font-weight-semibold)',
    backgroundColor: 'var(--mantine-color-brand-2)',
    borderRadius: 2,
    padding: '0 4px',
  }
}

export function TaskHoldDateRow({
  taskType,
  holdDate,
  changed,
  expanded,
  canEdit,
  editing,
  error = null,
  inputRef,
  onToggleEdit,
  onSelect,
}: Readonly<TaskHoldDateRowProps>) {
  const holdDateLabel = formatTaskDate(holdDate)

  return (
    <>
      <Group gap={6} wrap="nowrap">
        {/* The hidden note beside this says the change out loud for screen readers. */}
        <Text fz="xs" style={getHoldDateStyle(changed)}>
          {/* An unset hold date reads as "No Record", the app's convention for a
              null value, rather than borrowing the target date. */}
          Hold Date:{' '}
          {holdDateLabel ?? (
            <Text span inherit c="var(--link-color)" fs="italic">
              No Record
            </Text>
          )}
        </Text>
        {changed && <VisuallyHidden>Hold date updated</VisuallyHidden>}
        {/* Rescheduling only opens up once the task is held, per the design —
            which this row already guarantees. */}
        {expanded && canEdit && (
          <Tooltip label="Edit hold date" withArrow>
            <UnstyledButton
              c="brand.7"
              lh={1}
              // Toggles, so the pencil is also the way out of the date field —
              // Escape doesn't close a native date input.
              aria-label={`Edit hold date of task ${taskType}`}
              aria-expanded={editing}
              onClick={onToggleEdit}
            >
              <FontAwesomeIcon icon={faPenToSquare} size="sm" />
            </UnstyledButton>
          </Tooltip>
        )}
      </Group>
      {expanded && editing && (
        // Uncontrolled on purpose. A native date input reports an incomplete or
        // implausible date as a value the card declines to commit, and a controlled
        // input would then have React restore the previous value — resetting every
        // segment, so a half-typed month snapped back to mm. The field unmounts when
        // the pencil closes it, so each time it opens it picks up the current date.
        <TextInput
          ref={inputRef}
          type="date"
          size="xs"
          w={140}
          defaultValue={toDateInputValue(holdDate)}
          // Bounds the calendar — today and past days are greyed out, since the on-hold
          // proc refuses both — and marks an out-of-range date invalid to the host.
          // Typing past it is still possible, so the card guards the commit too.
          min={tomorrowDateInputValue()}
          max="9999-12-31"
          onChange={(event) => onSelect(event.currentTarget.value)}
          error={error ?? undefined}
          aria-label={`New hold date for task ${taskType}`}
        />
      )}
    </>
  )
}
